import { useEffect, useRef, useState } from 'react'
import { Download, FileSpreadsheet, FileText, Printer, Table2 } from 'lucide-react'
import {
  exportCsv,
  exportExcel,
  exportPdf,
  printRows,
  type ExportColumn,
  type ExportFormat,
} from '@/lib/export'
import { useToast } from './useToast'
import { Button } from './Button'
import styles from './ExportMenu.module.css'

interface ExportMenuProps<T> {
  columns: ExportColumn<T>[]
  /** Must return the complete filtered dataset, not just the visible page. */
  fetchRows: () => Promise<T[]>
  baseName: string
  title: string
  disabled?: boolean
}

const OPTIONS: { format: ExportFormat; label: string; icon: typeof Table2 }[] = [
  { format: 'csv', label: 'CSV', icon: Table2 },
  { format: 'excel', label: 'Excel', icon: FileSpreadsheet },
  { format: 'pdf', label: 'PDF', icon: FileText },
  { format: 'print', label: 'Print', icon: Printer },
]

/**
 * Export control.
 *
 * Always exports the whole filtered result: the rows are fetched at click time
 * rather than read off the screen, so what lands in the file matches what the
 * filters describe.
 */
export function ExportMenu<T>({ columns, fetchRows, baseName, title, disabled = false }: ExportMenuProps<T>) {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState<ExportFormat | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const toast = useToast()

  useEffect(() => {
    if (!open) return

    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const run = async (format: ExportFormat) => {
    setBusy(format)
    try {
      const rows = await fetchRows()

      if (rows.length === 0) {
        toast.info('Nothing to export', 'The current filters return no records.')
        return
      }

      switch (format) {
        case 'csv':
          exportCsv(columns, rows, baseName)
          break
        case 'excel':
          exportExcel(columns, rows, baseName, title)
          break
        case 'pdf':
          await exportPdf(columns, rows, baseName, title)
          break
        case 'print':
          printRows(columns, rows, title)
          break
      }

      if (format !== 'print') {
        toast.success('Export ready', `${rows.length} ${rows.length === 1 ? 'record' : 'records'} exported.`)
      }
    } catch (error) {
      toast.error('Export failed', error instanceof Error ? error.message : 'The export could not be produced.')
    } finally {
      setBusy(null)
      setOpen(false)
    }
  }

  return (
    <div className={styles.container} ref={containerRef}>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => setOpen((current) => !current)}
        disabled={disabled}
        loading={busy !== null}
        iconLeft={busy === null ? <Download aria-hidden="true" /> : undefined}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        Export
      </Button>

      {open ? (
        <div className={styles.menu} role="menu">
          {OPTIONS.map(({ format, label, icon: Icon }) => (
            <button
              key={format}
              type="button"
              role="menuitem"
              className={styles.item}
              onClick={() => void run(format)}
              disabled={busy !== null}
            >
              <Icon aria-hidden="true" />
              {label}
            </button>
          ))}
          <p className={styles.note}>Exports every record matching the current filters.</p>
        </div>
      ) : null}
    </div>
  )
}
